import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, MenuItem,
  CircularProgress, Alert, Paper, Grid, Divider,
  IconButton, Stack, LinearProgress, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, GlobalStyles,
  Snackbar,
  useMediaQuery, useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import BadgeIcon from '@mui/icons-material/Badge';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import CollectionsIcon from '@mui/icons-material/Collections';
import CloseIcon from '@mui/icons-material/Close';
import { preprocessDocumentImage } from '../../utils/imagePreprocess';

const API = process.env.REACT_APP_API_URL;

const TITLE_OPTIONS = ['นาย', 'นาง', 'นางสาว', 'Mr.', 'Mrs.', 'Miss'];
const GENDER_OPTIONS = ['ชาย', 'หญิง', 'อื่นๆ'];
const RELIGION_OPTIONS = ['พุทธ', 'อิสลาม', 'คริสต์', 'พราหมณ์-ฮินดู', 'ไม่ระบุ', 'อื่นๆ'];
const MARITAL_OPTIONS = ['โสด', 'สมรส', 'หย่าร้าง', 'หม้าย'];
const BANK_OPTIONS = ['กสิกรไทย', 'กรุงเทพ', 'กรุงไทย', 'กรุงศรีอยุธยา', 'ไทยพาณิชย์', 'ทีทีบี', 'ออมสิน', 'ธ.ก.ส.', 'ซีไอเอ็มบี', 'ยูโอบี', 'อื่นๆ'];
const DOC_CATEGORIES = [
  { key: 'id_card', label: 'บัตรประชาชน', accept: 'image/*,application/pdf' },
  { key: 'house_registration', label: 'ทะเบียนบ้าน', accept: 'image/*,application/pdf' },
  { key: 'education', label: 'วุฒิการศึกษา', accept: 'image/*,application/pdf' },
  { key: 'resume', label: 'Resume / CV', accept: 'image/*,application/pdf,.doc,.docx' },
  { key: 'certificate', label: 'ใบรับรอง / ใบอบรม', accept: 'image/*,application/pdf' },
  { key: 'other', label: 'เอกสารอื่นๆ', accept: 'image/*,application/pdf,.doc,.docx' },
];
const STEPS = [
  'รูปโปรไฟล์ & สแกนบัตร',
  'ข้อมูลส่วนตัว',
  'ที่อยู่',
  'บัญชีธนาคาร & ผู้ติดต่อ',
  'เอกสารแนบ',
  'บัญชีเข้าระบบ',
];

const ID_TW = 1011; const ID_TH = 638; const ID_TR = ID_TW / ID_TH;
function cropToIdCard(dataUrl, bbox) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const W = img.naturalWidth; const H = img.naturalHeight;
      let sx; let sy; let sw; let sh;
      const validBbox = Array.isArray(bbox) && bbox.length === 4 && bbox.every((v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1) && bbox[2] > bbox[0] && bbox[3] > bbox[1];
      if (validBbox) {
        const [x0, y0, x1, y1] = bbox;
        sx = x0 * W; sy = y0 * H; sw = (x1 - x0) * W; sh = (y1 - y0) * H;
        const r = sw / sh;
        if (r > ID_TR) { const nH = sw / ID_TR; sy -= (nH - sh) / 2; sh = nH; } else { const nW = sh * ID_TR; sx -= (nW - sw) / 2; sw = nW; }
      } else {
        const r = W / H;
        if (r > ID_TR) { sw = H * ID_TR; sh = H; sx = (W - sw) / 2; sy = 0; } else { sw = W; sh = W / ID_TR; sx = 0; sy = (H - sh) / 2; }
      }
      if (sx < 0) sx = 0; if (sy < 0) sy = 0;
      if (sx + sw > W) sx = Math.max(0, W - sw); if (sy + sh > H) sy = Math.max(0, H - sh);
      sw = Math.min(sw, W - sx); sh = Math.min(sh, H - sy);
      const canvas = document.createElement('canvas'); canvas.width = ID_TW; canvas.height = ID_TH;
      const ctx = canvas.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, ID_TW, ID_TH);
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, ID_TW, ID_TH);
      resolve(canvas.toDataURL('image/jpeg', 0.92));
    };
    img.src = dataUrl;
  });
}

const defaultForm = {
  title: '', firstname_th: '', lastname_th: '', firstname: '', lastname: '',
  gender: '', religion: '', marital_status: '', children_count: 0,
  date_of_birth: '', tax_id: '', phone_number: '',
  address: { house_no: '', subdistrict: '', district: '', province: '', zipcode: '' },
  bank_account: { bank_name: '', account_name: '', number: '', account_type: 'ออมทรัพย์' },
  emergency_contact: { name: '', phone: '', relationship: '' },
  username: '', password: '', confirm_password: '',
};

export default function OnboardingForm() {
  const { token } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tokenStatus, setTokenStatus] = useState('checking');
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(defaultForm);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [stepError, setStepError] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  // Profile image
  const [profileUrl, setProfileUrl] = useState('');
  const [profileUploading, setProfileUploading] = useState(false);
  const profileRef = useRef();

  // Documents
  const [docs, setDocs] = useState({});
  const docRefs = useRef({});

  // Duplicate / availability
  const [usernameStatus, setUsernameStatus] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const dupTimerRef = useRef({});

  // ID scan state machine: 'idle' | 'camera' | 'preview'
  const [scanOpen, setScanOpen] = useState(false);
  const [scanMode, setScanMode] = useState('idle');
  const [facingMode, setFacingMode] = useState('environment');
  const [scanImage, setScanImage] = useState(null);
  const [scanMime, setScanMime] = useState('image/jpeg');
  const [scanPreviewUrl, setScanPreviewUrl] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState('');
  const idFileRef = useRef();
  const videoRef = useRef(null);
  const camStreamRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/onboarding/${token}`)
      .then(() => setTokenStatus('valid'))
      .catch((err) => {
        const msg = err.response?.data?.error?.message || err.response?.data?.message || '';
        setTokenStatus(msg.includes('ถูกใช้งานแล้ว') ? 'used' : 'invalid');
      });
  }, [token]);

  // Assign stream to video element once camera view is rendered
  useEffect(() => {
    if (scanMode === 'camera' && videoRef.current && camStreamRef.current) {
      videoRef.current.srcObject = camStreamRef.current;
    }
  }, [scanMode]);

  // Stop camera when component unmounts
  useEffect(() => () => stopCamera(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setNested = (group, field) => (e) => setForm((f) => ({ ...f, [group]: { ...f[group], [field]: e.target.value } }));

  const handleUsernameChange = (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, username: val }));
    setUsernameStatus(null);
    clearTimeout(dupTimerRef.current.username);
    if (!val) return;
    setUsernameStatus('checking');
    dupTimerRef.current.username = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/onboarding/check-username/${encodeURIComponent(val)}`);
        const available = data?.result?.available ?? data?.available;
        setUsernameStatus(available ? 'available' : 'taken');
      } catch { setUsernameStatus(null); }
    }, 600);
  };

  const triggerDupCheck = (nextForm) => {
    clearTimeout(dupTimerRef.current.dup);
    dupTimerRef.current.dup = setTimeout(async () => {
      const { tax_id, firstname_th, lastname_th } = nextForm;
      if (!tax_id && !(firstname_th && lastname_th)) return;
      try {
        const { data } = await axios.post(`${API}/onboarding/check-duplicate`, { tax_id, firstname_th, lastname_th });
        const conflicts = data?.result?.conflicts ?? data?.conflicts ?? [];
        const errs = {};
        conflicts.forEach((c) => { errs[c.field] = c.message; });
        setFieldErrors(errs);
      } catch { /* ignore */ }
    }, 700);
  };

  const setWithDupCheck = (field) => (e) => {
    const val = e.target.value;
    setForm((f) => {
      const updated = { ...f, [field]: val };
      triggerDupCheck(updated);
      return updated;
    });
  };

  // ── Camera ───────────────────────────────────────────────────
  const stopCamera = () => {
    if (camStreamRef.current) {
      camStreamRef.current.getTracks().forEach((t) => t.stop());
      camStreamRef.current = null;
    }
  };

  const startCamera = async (facing) => {
    const mode = facing || facingMode;
    setScanError('');
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      camStreamRef.current = stream;
      setScanMode('camera');
    } catch {
      setScanError('ไม่สามารถเข้าถึงกล้องได้ กรุณาอนุญาตการใช้กล้อง หรือเลือกรูปจากคลังแทน');
    }
  };

  const captureFrame = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    stopCamera();
    setScanPreviewUrl(dataUrl);
    setScanImage(dataUrl.split(',')[1]);
    setScanMime('image/jpeg');
    setScanMode('preview');
  };

  const flipCamera = () => {
    const next = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(next);
    startCamera(next);
  };

  // ── Profile upload ────────────────────────────────────────────
  const handleProfileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfileUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/onboarding/upload-doc`, fd);
      setProfileUrl(data?.result?.url || data?.url);
    } catch { setStepError('อัปโหลดรูปโปรไฟล์ไม่สำเร็จ'); }
    finally { setProfileUploading(false); e.target.value = ''; }
  };

  // ── Doc upload ───────────────────────────────────────────────
  const handleDocUpload = async (category, file) => {
    if (!file) return;
    setDocs((p) => ({ ...p, [category]: { ...p[category], uploading: true } }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/onboarding/upload-doc`, fd);
      const url = data?.result?.url || data?.url;
      setDocs((p) => ({ ...p, [category]: { url, name: file.name, uploading: false } }));
    } catch {
      setDocs((p) => ({ ...p, [category]: { ...p[category], uploading: false } }));
      setStepError('อัปโหลดไฟล์ไม่สำเร็จ');
    }
  };

  // ── ID scan ──────────────────────────────────────────────────
  const handleScanImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScanResult(null); setScanError(''); setScanMime(file.type || 'image/jpeg');
    const reader = new FileReader();
    reader.onload = (ev) => {
      setScanPreviewUrl(ev.target.result);
      setScanImage(ev.target.result.split(',')[1]);
      setScanMode('preview');
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleScanAnalyze = async () => {
    if (!scanImage) return;
    setScanLoading(true); setScanError(''); setScanResult(null);
    try {
      const base64 = await preprocessDocumentImage(`data:${scanMime};base64,${scanImage}`);
      const { data } = await axios.post(`${API}/ai/scan-id-card`, { image: base64, mimeType: 'image/jpeg' });
      setScanResult(data?.result ?? data);
    } catch (err) { setScanError(err?.response?.data?.error?.message || 'วิเคราะห์ไม่สำเร็จ กรุณาลองใหม่'); }
    finally { setScanLoading(false); }
  };

  const handleScanFill = async () => {
    if (!scanResult) return;
    const r = scanResult;
    setForm((f) => ({
      ...f,
      title: r.title || f.title,
      firstname_th: r.firstname_th || f.firstname_th,
      lastname_th: r.lastname_th || f.lastname_th,
      firstname: r.firstname || f.firstname,
      lastname: r.lastname || f.lastname,
      tax_id: r.tax_id || f.tax_id,
      date_of_birth: r.date_of_birth || f.date_of_birth,
      gender: r.gender || f.gender,
      religion: r.religion || f.religion || 'ไม่ระบุ',
      address: {
        ...f.address,
        house_no: r.address?.house_number || r.address?.house_no || f.address.house_no,
        subdistrict: r.address?.subdistrict || f.address.subdistrict,
        district: r.address?.district || f.address.district,
        province: r.address?.province || f.address.province,
      },
    }));
    if (scanPreviewUrl) {
      try {
        const cropped = await cropToIdCard(scanPreviewUrl, r.card_bbox);
        const blob = await fetch(cropped).then((res) => res.blob());
        handleDocUpload('id_card', new File([blob], 'id_card_scan.jpg', { type: 'image/jpeg' }));
      } catch { /* ignore */ }
    }
    handleScanClose();
  };

  const handleScanClose = () => {
    stopCamera();
    setScanOpen(false);
    setScanMode('idle');
    setScanImage(null);
    setScanPreviewUrl(null);
    setScanResult(null);
    setScanError('');
  };

  // ── Camera view with card frame overlay ──────────────────────
  const renderCameraView = () => (
    <Box sx={{ position: 'relative', width: '100%', bgcolor: '#000', overflow: 'hidden', aspectRatio: { xs: '3/4', sm: '4/3' } }}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />

      {/* Card frame — uses box-shadow to darken everything outside the card area */}
      <Box sx={{
        position: 'absolute',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -54%)',
        width: 'min(86%, 370px)',
        aspectRatio: '1.585',
        boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
        borderRadius: '8px',
        zIndex: 1,
        pointerEvents: 'none',
      }}>
        {/* Corner brackets */}
        {[
          { top: -2, left: -2, borderTop: '3px solid #fff', borderLeft: '3px solid #fff', borderRadius: '4px 0 0 0' },
          { top: -2, right: -2, borderTop: '3px solid #fff', borderRight: '3px solid #fff', borderRadius: '0 4px 0 0' },
          { bottom: -2, left: -2, borderBottom: '3px solid #fff', borderLeft: '3px solid #fff', borderRadius: '0 0 0 4px' },
          { bottom: -2, right: -2, borderBottom: '3px solid #fff', borderRight: '3px solid #fff', borderRadius: '0 0 4px 0' },
        ].map((sx, i) => (
          <Box key={i} sx={{ position: 'absolute', width: 24, height: 24, ...sx }} />
        ))}

        {/* Scanning line animation */}
        <Box sx={{
          position: 'absolute',
          left: 6, right: 6, height: '2px',
          background: 'linear-gradient(90deg, transparent 0%, #42a5f5 20%, #90caf9 50%, #42a5f5 80%, transparent 100%)',
          boxShadow: '0 0 10px 2px rgba(66,165,245,0.7)',
          animation: 'idScanLine 2.4s ease-in-out infinite',
          top: 0,
        }} />
      </Box>

      {/* Guide text */}
      <Typography sx={{
        position: 'absolute', bottom: 90, left: 0, right: 0,
        textAlign: 'center', color: 'white', fontSize: 13, letterSpacing: 0.2,
        textShadow: '0 1px 6px rgba(0,0,0,0.9)',
        zIndex: 2, pointerEvents: 'none',
      }}>
        วางบัตรประชาชนในกรอบ แล้วกดถ่ายภาพ
      </Typography>

      {/* Bottom controls bar */}
      <Box sx={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        px: 3, py: 2, zIndex: 2,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
      }}>
        {/* Close */}
        <IconButton
          onClick={() => { stopCamera(); setScanMode('idle'); setScanError(''); }}
          sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
        >
          <CloseIcon />
        </IconButton>

        {/* Shutter button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <IconButton
            onClick={captureFrame}
            sx={{
              bgcolor: 'white',
              width: 68, height: 68,
              border: '4px solid rgba(255,255,255,0.45)',
              boxShadow: '0 0 0 2px rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'grey.100', transform: 'scale(1.05)' },
              transition: 'transform 0.1s',
            }}
          >
            <PhotoCameraIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          </IconButton>
        </Box>

        {/* Flip camera */}
        <IconButton
          onClick={flipCamera}
          sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
        >
          <FlipCameraAndroidIcon />
        </IconButton>
      </Box>
    </Box>
  );

  // ── Scan dialog ──────────────────────────────────────────────
  const renderScanDialog = () => (
    <Dialog
      open={scanOpen}
      onClose={handleScanClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: scanMode === 'camera' ? { overflow: 'hidden' } : {} }}
    >
      <GlobalStyles styles={{
        '@keyframes idScanLine': {
          '0%': { top: '0%' },
          '50%': { top: 'calc(100% - 2px)' },
          '100%': { top: '0%' },
        },
      }} />

      {scanMode !== 'camera' && (
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <BadgeIcon color="primary" />
              <Typography fontWeight="bold">สแกนบัตรประชาชน</Typography>
            </Stack>
            <IconButton size="small" onClick={handleScanClose}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        </DialogTitle>
      )}

      <DialogContent sx={{ p: scanMode === 'camera' ? 0 : undefined }}>
        {scanMode === 'camera' ? renderCameraView() : (
          <Stack spacing={2} sx={{ pt: 1 }}>
            {scanMode === 'idle' && (
              <>
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => startCamera()}
                  sx={{ py: 1.5 }}
                >
                  ถ่ายรูปด้วยกล้อง
                </Button>
                <input ref={idFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleScanImageChange} />
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<CollectionsIcon />}
                  onClick={() => idFileRef.current?.click()}
                >
                  เลือกรูปจากคลัง
                </Button>
              </>
            )}

            {scanMode === 'preview' && scanPreviewUrl && (
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={scanPreviewUrl}
                  alt="id-preview"
                  style={{ maxWidth: '100%', maxHeight: 260, borderRadius: 8, border: '1px solid #ddd' }}
                />
                <Button size="small" variant="text" sx={{ mt: 1, display: 'block', mx: 'auto' }}
                  onClick={() => { setScanMode('idle'); setScanImage(null); setScanPreviewUrl(null); setScanResult(null); setScanError(''); }}>
                  เปลี่ยนรูป
                </Button>
              </Box>
            )}

            {scanError && <Alert severity="error">{scanError}</Alert>}

            {scanResult && (
              <Alert severity="success">
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>ข้อมูลที่อ่านได้</Typography>
                {scanResult.title && <div>คำนำหน้า: <b>{scanResult.title}</b></div>}
                {scanResult.firstname_th && <div>ชื่อ (TH): <b>{scanResult.firstname_th} {scanResult.lastname_th}</b></div>}
                {scanResult.firstname && <div>ชื่อ (EN): <b>{scanResult.firstname} {scanResult.lastname}</b></div>}
                {scanResult.tax_id && <div>เลขบัตร: <b>{scanResult.tax_id}</b></div>}
                {scanResult.date_of_birth && <div>วันเกิด: <b>{scanResult.date_of_birth}</b></div>}
                {scanResult.gender && <div>เพศ: <b>{scanResult.gender}</b></div>}
                {scanResult.religion && <div>ศาสนา: <b>{scanResult.religion}</b></div>}
                {scanResult.address?.province && <div>จังหวัด: <b>{scanResult.address.province}</b></div>}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      {scanMode !== 'camera' && (
        <DialogActions>
          {scanResult ? (
            <Button variant="contained" color="success" onClick={handleScanFill}>
              กรอกข้อมูลลงฟอร์ม
            </Button>
          ) : scanMode === 'preview' && (
            <Button
              variant="contained"
              onClick={handleScanAnalyze}
              disabled={!scanImage || scanLoading}
              startIcon={scanLoading ? <CircularProgress size={16} color="inherit" /> : <BadgeIcon />}
            >
              {scanLoading ? 'กำลังวิเคราะห์...' : 'วิเคราะห์ด้วย AI'}
            </Button>
          )}
          <Button onClick={handleScanClose} color="inherit">ปิด</Button>
        </DialogActions>
      )}
    </Dialog>
  );

  // ── Step content ─────────────────────────────────────────────
  const renderStep0 = () => (
    <Stack spacing={3} alignItems="center">
      {/* Profile photo */}
      <Box sx={{ textAlign: 'center' }}>
        <Avatar src={profileUrl} sx={{ width: 110, height: 110, mx: 'auto', mb: 2, border: '3px solid', borderColor: profileUrl ? 'primary.main' : 'divider', fontSize: 40 }}>
          {!profileUrl && <PhotoCameraIcon sx={{ fontSize: 40, opacity: 0.4 }} />}
        </Avatar>
        <input ref={profileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleProfileUpload} />
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button variant={profileUrl ? 'outlined' : 'contained'} size="large" startIcon={profileUploading ? <CircularProgress size={18} color="inherit" /> : <PhotoCameraIcon />}
            onClick={() => profileRef.current?.click()} disabled={profileUploading} sx={{ minWidth: 140 }}>
            {profileUploading ? 'กำลังอัปโหลด...' : profileUrl ? 'เปลี่ยนรูป' : 'เพิ่มรูปโปรไฟล์'}
          </Button>
          {profileUrl && <Button size="large" color="error" variant="outlined" onClick={() => setProfileUrl('')}>ลบ</Button>}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>ไม่บังคับ</Typography>
      </Box>

      <Divider flexItem />

      {/* ID card scan */}
      <Box sx={{ width: '100%', bgcolor: 'primary.50', border: '1.5px dashed', borderColor: 'primary.300', borderRadius: 3, p: 3, textAlign: 'center' }}>
        <BadgeIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>สแกนบัตรประชาชน</Typography>
        <Typography variant="body2" color="text.secondary" mb={2.5}>
          AI จะอ่านข้อมูลจากบัตรและกรอกให้อัตโนมัติ<br />ประหยัดเวลาได้มาก!
        </Typography>
        <Button variant="contained" size="large" startIcon={<BadgeIcon />} onClick={() => setScanOpen(true)} fullWidth sx={{ py: 1.5, borderRadius: 2, fontSize: 16 }}>
          ถ่ายรูป / อัปโหลดบัตร
        </Button>
        <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>ข้ามได้ กรอกเองในขั้นตอนถัดไป</Typography>
      </Box>
    </Stack>
  );

  const renderStep1 = () => (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}>ชื่อ-นามสกุล *</Typography>
        <Stack spacing={2} mt={1}>
          <TextField select fullWidth label="คำนำหน้า" value={form.title} onChange={set('title')}>
            {TITLE_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="ชื่อ (ภาษาไทย) *" value={form.firstname_th}
            onChange={setWithDupCheck('firstname_th')} required error={!!fieldErrors.name} />
          <TextField fullWidth label="นามสกุล (ภาษาไทย) *" value={form.lastname_th}
            onChange={setWithDupCheck('lastname_th')} required error={!!fieldErrors.name} helperText={fieldErrors.name || ''} />
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label="First Name" value={form.firstname} onChange={set('firstname')} />
            <TextField fullWidth label="Last Name" value={form.lastname} onChange={set('lastname')} />
          </Stack>
        </Stack>
      </Box>
      <Divider />
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}>ข้อมูลส่วนตัว</Typography>
        <Stack spacing={2} mt={1}>
          <Stack direction="row" spacing={2}>
            <TextField select fullWidth label="เพศ" value={form.gender} onChange={set('gender')}>
              {GENDER_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="สถานภาพ" value={form.marital_status} onChange={set('marital_status')}>
              {MARITAL_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField select fullWidth label="ศาสนา" value={form.religion} onChange={set('religion')}>
            {RELIGION_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label="วันเกิด" type="date" value={form.date_of_birth} onChange={set('date_of_birth')} InputLabelProps={{ shrink: true }} sx={{ flex: 7 }} />
            <TextField fullWidth label="จำนวนบุตร" type="number" value={form.children_count} onChange={set('children_count')} inputProps={{ min: 0 }} sx={{ flex: 5 }} />
          </Stack>
        </Stack>
      </Box>
      <Divider />
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}>ติดต่อ & บัตรประชาชน *</Typography>
        <Stack spacing={2} mt={1}>
          <TextField fullWidth label="เลขบัตรประชาชน 13 หลัก *" value={form.tax_id}
            onChange={setWithDupCheck('tax_id')} required inputProps={{ maxLength: 13, inputMode: 'numeric' }}
            error={!!fieldErrors.tax_id} helperText={fieldErrors.tax_id || ''} />
          <TextField fullWidth label="เบอร์โทรศัพท์ *" value={form.phone_number} onChange={set('phone_number')}
            required inputProps={{ inputMode: 'tel' }} />
        </Stack>
      </Box>
    </Stack>
  );

  const renderStep2 = () => (
    <Stack spacing={2}>
      <TextField fullWidth label="บ้านเลขที่ / ซอย / ถนน" value={form.address.house_no} onChange={setNested('address', 'house_no')} />
      <Stack direction="row" spacing={2}>
        <TextField fullWidth label="ตำบล / แขวง" value={form.address.subdistrict} onChange={setNested('address', 'subdistrict')} />
        <TextField fullWidth label="อำเภอ / เขต" value={form.address.district} onChange={setNested('address', 'district')} />
      </Stack>
      <Stack direction="row" spacing={2}>
        <TextField fullWidth label="จังหวัด" value={form.address.province} onChange={setNested('address', 'province')} sx={{ flex: 2 }} />
        <TextField fullWidth label="รหัสไปรษณีย์" value={form.address.zipcode} onChange={setNested('address', 'zipcode')} inputProps={{ inputMode: 'numeric', maxLength: 5 }} sx={{ flex: 1 }} />
      </Stack>
    </Stack>
  );

  const renderStep3 = () => (
    <Stack spacing={3}>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}>บัญชีธนาคาร</Typography>
        <Stack spacing={2} mt={1}>
          <TextField select fullWidth label="ธนาคาร" value={form.bank_account.bank_name} onChange={setNested('bank_account', 'bank_name')}>
            {BANK_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          <TextField select fullWidth label="ประเภทบัญชี" value={form.bank_account.account_type} onChange={setNested('bank_account', 'account_type')}>
            {['ออมทรัพย์', 'กระแสรายวัน'].map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="เลขบัญชี" value={form.bank_account.number} onChange={setNested('bank_account', 'number')} inputProps={{ inputMode: 'numeric' }} />
          <TextField fullWidth label="ชื่อบัญชี (ตามสมุดบัญชี)" value={form.bank_account.account_name} onChange={setNested('bank_account', 'account_name')} />
        </Stack>
      </Box>
      <Divider />
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}>ผู้ติดต่อฉุกเฉิน</Typography>
        <Stack spacing={2} mt={1}>
          <TextField fullWidth label="ชื่อ-นามสกุล" value={form.emergency_contact.name} onChange={setNested('emergency_contact', 'name')} />
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label="เบอร์โทร" value={form.emergency_contact.phone} onChange={setNested('emergency_contact', 'phone')} inputProps={{ inputMode: 'tel' }} />
            <TextField fullWidth label="ความสัมพันธ์" value={form.emergency_contact.relationship} onChange={setNested('emergency_contact', 'relationship')} />
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );

  const renderStep4 = () => (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>อัปโหลดไฟล์ภาพหรือ PDF ขนาดไม่เกิน 5MB — ไม่บังคับทุกประเภท</Alert>
      {DOC_CATEGORIES.map(({ key, label, accept }) => {
        const doc = docs[key];
        return (
          <Box key={key} sx={{
            p: 2, borderRadius: 2, border: '1.5px solid',
            borderColor: doc?.url ? 'success.main' : 'divider',
            bgcolor: doc?.url ? 'success.50' : 'background.paper',
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="bold">{label}</Typography>
                {doc?.uploading && <LinearProgress sx={{ mt: 0.5, borderRadius: 2 }} />}
                {doc?.url && <Typography variant="caption" color="success.main">✓ {doc.name || 'อัปโหลดแล้ว'}</Typography>}
              </Box>
              {doc?.url ? (
                <IconButton color="error" onClick={() => setDocs((p) => { const n = { ...p }; delete n[key]; return n; })}>
                  <DeleteIcon />
                </IconButton>
              ) : (
                <>
                  <input ref={(el) => { docRefs.current[key] = el; }} type="file" accept={accept} style={{ display: 'none' }}
                    onChange={(e) => { handleDocUpload(key, e.target.files[0]); e.target.value = ''; }} />
                  <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => docRefs.current[key]?.click()} disabled={doc?.uploading} sx={{ whiteSpace: 'nowrap', minWidth: 110 }}>
                    อัปโหลด
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );

  const renderStep5 = () => (
    <Stack spacing={3}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        <b>ไม่บังคับ</b> — HR สามารถสร้าง username ให้ทีหลังได้ กรอกเฉพาะถ้าต้องการ login เอง
      </Alert>
      <TextField fullWidth label="Username" value={form.username} onChange={handleUsernameChange}
        autoComplete="off"
        error={usernameStatus === 'taken'}
        helperText={
          usernameStatus === 'checking' ? 'กำลังตรวจสอบ...' :
          usernameStatus === 'taken' ? '✗ Username นี้ถูกใช้งานแล้ว' :
          usernameStatus === 'available' ? '✓ Username นี้ใช้ได้' :
          'ภาษาอังกฤษ ตัวเลข ไม่มีช่องว่าง'
        }
        FormHelperTextProps={{ style: { color: usernameStatus === 'available' ? 'green' : usernameStatus === 'taken' ? 'red' : undefined } }}
        inputProps={{ pattern: '[a-zA-Z0-9_.-]+' }}
      />
      <TextField fullWidth label="Password" value={form.password} onChange={set('password')}
        type={showPassword ? 'text' : 'password'} autoComplete="new-password"
        helperText="อย่างน้อย 6 ตัวอักษร"
        InputProps={{ endAdornment: <IconButton onClick={() => setShowPassword((s) => !s)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton> }}
      />
      <TextField fullWidth label="ยืนยัน Password" value={form.confirm_password} onChange={set('confirm_password')}
        type={showPassword ? 'text' : 'password'} autoComplete="new-password"
        error={!!form.confirm_password && form.password !== form.confirm_password}
        helperText={form.confirm_password && form.password !== form.confirm_password ? 'รหัสผ่านไม่ตรงกัน' : ''}
      />
      <Divider />
      <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>สรุปข้อมูล</Typography>
        <Stack spacing={0.5}>
          {[
            ['ชื่อ-นามสกุล', `${form.firstname_th || '-'} ${form.lastname_th || ''}`],
            ['เบอร์โทร', form.phone_number || '-'],
            ['เลขบัตรประชาชน', form.tax_id || '-'],
            ['เอกสาร', `${Object.values(docs).filter((d) => d?.url).length} ไฟล์`],
            ...(form.username ? [['Username', `@${form.username}`]] : []),
          ].map(([label, val]) => (
            <Stack key={label} direction="row" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="caption" fontWeight="medium">{val}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );

  const STEP_CONTENT = [renderStep0, renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];

  // ── Step validation ──────────────────────────────────────────
  const validateStep = () => {
    setStepError('');
    if (activeStep === 1) {
      if (!form.firstname_th || !form.lastname_th) { setStepError('กรุณากรอกชื่อ-นามสกุล (ภาษาไทย)'); return false; }
      if (!form.tax_id) { setStepError('กรุณากรอกเลขบัตรประชาชน'); return false; }
      if (!form.phone_number) { setStepError('กรุณากรอกเบอร์โทรศัพท์'); return false; }
      if (Object.keys(fieldErrors).length > 0) { setStepError(Object.values(fieldErrors)[0]); return false; }
    }
    if (activeStep === 5) {
      if (usernameStatus === 'taken') { setStepError('Username นี้ถูกใช้งานแล้ว กรุณาเปลี่ยน'); return false; }
      if (form.username && form.password !== form.confirm_password) { setStepError('รหัสผ่านและยืนยันรหัสผ่านไม่ตรงกัน'); return false; }
      if (form.username && form.password.length < 6) { setStepError('รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'); return false; }
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    // Step 1 — ตรวจ tax_id / ชื่อ-นามสกุลซ้ำกับ database ก่อนไป step ถัดไป
    if (activeStep === 1) {
      setSubmitting(true);
      try {
        const { data } = await axios.post(`${API}/onboarding/check-duplicate`, {
          tax_id: form.tax_id,
          firstname_th: form.firstname_th,
          lastname_th: form.lastname_th,
        });
        if (data.conflicts && data.conflicts.length > 0) {
          setStepError(data.conflicts[0].message);
          return;
        }
      } catch (err) {
        setStepError(err.response?.data?.error?.message || err.response?.data?.message || 'ตรวจสอบข้อมูลไม่สำเร็จ');
        return;
      } finally {
        setSubmitting(false);
      }
    }

    setActiveStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setStepError('');
    setActiveStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    const documents = Object.entries(docs).filter(([, v]) => v?.url).map(([category, v]) => ({
      category, label: DOC_CATEGORIES.find((d) => d.key === category)?.label || category, file_url: v.url,
    }));
    const payload = { ...form, documents };
    if (profileUrl) payload.profile_image_url = profileUrl;
    delete payload.confirm_password;
    try {
      await axios.post(`${API}/onboarding/${token}`, payload);
      setSubmitted(true);
      setSnack({ open: true, message: 'ส่งข้อมูลเรียบร้อยแล้ว! HR จะตรวจสอบและยืนยันในลำดับถัดไป', severity: 'success' });
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.response?.data?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
      setStepError(msg);
      setSnack({ open: true, message: msg, severity: 'error' });
    } finally { setSubmitting(false); }
  };

  const STEP_ICONS = ['📷', '👤', '🏠', '🏦', '📄', '🔑'];

  // ── Status screens ────────────────────────────────────────────
  if (tokenStatus === 'checking') return (
    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" gap={2}>
      <CircularProgress size={48} />
      <Typography color="text.secondary">กำลังตรวจสอบ...</Typography>
    </Box>
  );

  if (tokenStatus === 'used') return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2} bgcolor="background.default">
      <Box sx={{ maxWidth: 400, width: '100%', textAlign: 'center', px: 2 }}>
        <Box sx={{ fontSize: 72, mb: 2 }}>⚠️</Box>
        <Typography variant="h5" fontWeight="bold" gutterBottom>ลิ้งนี้ใช้ไปแล้ว</Typography>
        <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
          ลิ้งนี้ถูกใช้สร้างข้อมูลพนักงานในระบบแล้ว<br />
          แต่ละลิ้งใช้ได้เพียง <b>1 ครั้ง</b> เท่านั้น
        </Typography>
        <Paper sx={{ p: 2, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            หากยังไม่ได้กรอกข้อมูล หรือมีข้อผิดพลาด<br />
            กรุณาติดต่อ HR เพื่อขอลิ้งใหม่
          </Typography>
        </Paper>
      </Box>
    </Box>
  );

  if (tokenStatus === 'invalid') return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2} bgcolor="background.default">
      <Box sx={{ maxWidth: 400, width: '100%', textAlign: 'center', px: 2 }}>
        <Box sx={{ fontSize: 72, mb: 2 }}>❌</Box>
        <Typography variant="h5" fontWeight="bold" gutterBottom>ลิ้งไม่ถูกต้อง</Typography>
        <Typography color="text.secondary">กรุณาตรวจสอบลิ้งที่ได้รับจาก HR อีกครั้ง</Typography>
      </Box>
    </Box>
  );

  if (submitted) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2} bgcolor="background.default">
      <Box sx={{ maxWidth: 400, width: '100%', textAlign: 'center', px: 2 }}>
        <Box sx={{ fontSize: 80, mb: 2 }}>🎉</Box>
        <Typography variant="h5" fontWeight="bold" gutterBottom>ส่งข้อมูลสำเร็จ!</Typography>
        <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
          HR จะตรวจสอบและยืนยันข้อมูลของคุณ<br />ในลำดับถัดไป
        </Typography>
        <Paper sx={{ p: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">หากมีข้อสงสัยกรุณาติดต่อ HR</Typography>
        </Paper>
      </Box>
    </Box>
  );

  // ── Main stepper form ─────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: { xs: 'background.default', sm: 'grey.100' }, display: 'flex', flexDirection: 'column' }}>
      <Snackbar open={snack.open} autoHideDuration={6000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>{snack.message}</Alert>
      </Snackbar>

      {renderScanDialog()}

      {/* ── Header ── */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', px: { xs: 2, sm: 4 }, pt: { xs: 3, sm: 4 }, pb: { xs: 2, sm: 3 } }}>
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1, textTransform: 'uppercase' }}>CorePlan ERP</Typography>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>แบบฟอร์มพนักงานใหม่</Typography>

          {/* Step dots */}
          <Stack direction="row" spacing={0.75} alignItems="center" mb={1.5}>
            {STEPS.map((_, i) => (
              <Box key={i} sx={{
                width: i === activeStep ? 24 : 8, height: 8,
                borderRadius: 4,
                bgcolor: i < activeStep ? 'rgba(255,255,255,0.9)' : i === activeStep ? 'white' : 'rgba(255,255,255,0.3)',
                transition: 'all 0.3s ease',
              }} />
            ))}
            <Typography variant="caption" sx={{ ml: 1, opacity: 0.85 }}>{activeStep + 1}/{STEPS.length}</Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontSize: 20 }}>{STEP_ICONS[activeStep]}</Typography>
            <Typography variant="subtitle1" fontWeight="bold">{STEPS[activeStep]}</Typography>
          </Stack>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ flex: 1, px: { xs: 2, sm: 4 }, py: { xs: 2.5, sm: 3 }, pb: { xs: 12, sm: 4 } }}>
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          {/* Desktop stepper */}
          {!isMobile && (
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, bgcolor: 'transparent' }}>
              {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
            </Stepper>
          )}

          {stepError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setStepError('')}>{stepError}</Alert>
          )}

          <Paper elevation={isMobile ? 0 : 2} sx={{ p: { xs: 0, sm: 3 }, borderRadius: { xs: 0, sm: 3 }, bgcolor: { xs: 'transparent', sm: 'background.paper' } }}>
            {STEP_CONTENT[activeStep]()}
          </Paper>
        </Box>
      </Box>

      {/* ── Bottom navigation (fixed on mobile, static on desktop) ── */}
      <Box sx={{
        position: { xs: 'fixed', sm: 'static' },
        bottom: 0, left: 0, right: 0,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        px: { xs: 2, sm: 4 },
        py: { xs: 1.5, sm: 2 },
        boxShadow: { xs: '0 -4px 20px rgba(0,0,0,0.08)', sm: 'none' },
      }}>
        <Box sx={{ maxWidth: 640, mx: 'auto' }}>
          <Stack direction="row" spacing={2}>
            <Button onClick={handleBack} disabled={activeStep === 0} startIcon={<KeyboardArrowLeft />} variant="outlined" color="inherit"
              sx={{ flex: 1, py: { xs: 1.5, sm: 1 }, borderRadius: 2, fontSize: { xs: 15, sm: 14 } }}>
              ย้อนกลับ
            </Button>
            {activeStep < STEPS.length - 1 ? (
              <Button onClick={handleNext} variant="contained" endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <KeyboardArrowRight />}
                disabled={submitting} sx={{ flex: 2, py: { xs: 1.5, sm: 1 }, borderRadius: 2, fontSize: { xs: 15, sm: 14 } }}>
                {submitting ? 'กำลังตรวจสอบ...' : 'ถัดไป'}
              </Button>
            ) : (
              <Button onClick={handleSubmit} variant="contained" color="success" disabled={submitting}
                startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                sx={{ flex: 2, py: { xs: 1.5, sm: 1 }, borderRadius: 2, fontSize: { xs: 15, sm: 14 } }}>
                {submitting ? 'กำลังส่ง...' : '✓ ส่งข้อมูล'}
              </Button>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
